import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationApi } from '../api/notificationApi';
import { Notification, NotificationResponse } from '../types/notifications';

export const useNotifications = (userEmail: string) => {
  const queryClient = useQueryClient();

  // Fetch notifications
  const {
    data: notificationData,
    isLoading,
    error,
    isFetched,
  } = useQuery({
    queryKey: ['notifications', userEmail],
    queryFn: () => notificationApi.getNotifications(userEmail),
    enabled: !!userEmail,
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: false, // Don't retry on errors - show error state instead
    staleTime: 10000, // Consider data stale after 10 seconds
    gcTime: 300000, // Keep data in cache for 5 minutes
  });

  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: number) => notificationApi.markAsRead(notificationId, userEmail),
    onSuccess: (_, notificationId) => {
      // Optimistically update the cache
      queryClient.setQueryData(['notifications', userEmail], (oldData: NotificationResponse | undefined) => {
        if (!oldData) return oldData;
        
        return {
          ...oldData,
          notifications: oldData.notifications.map((n: Notification) =>
            n.id === notificationId ? { ...n, isRead: true } : n
          ),
          unreadCount: Math.max(0, oldData.unreadCount - 1),
        };
      });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationApi.markAllAsRead(userEmail),
    onSuccess: () => {
      // Optimistically update the cache
      queryClient.setQueryData(['notifications', userEmail], (oldData: NotificationResponse | undefined) => {
        if (!oldData) return oldData;
        
        return {
          ...oldData,
          notifications: oldData.notifications.map((n: Notification) => ({
            ...n,
            isRead: true,
          })),
          unreadCount: 0,
        };
      });
    },
  });

  // Remove notification mutation
  const removeNotificationMutation = useMutation({
    mutationFn: (notificationId: number) => notificationApi.removeNotification(notificationId, userEmail),
    onSuccess: (_, notificationId) => {
      // Optimistically update the cache
      queryClient.setQueryData(['notifications', userEmail], (oldData: NotificationResponse | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          notifications: oldData.notifications.filter((n: Notification) => n.id !== notificationId),
          unreadCount: oldData.notifications.filter((n: Notification) => !n.isRead && n.id !== notificationId).length,
        };
      });
    },
  });

  // Remove all notifications mutation
  const removeAllNotificationsMutation = useMutation({
    mutationFn: () => notificationApi.removeAllNotifications(userEmail),
    onSuccess: () => {
      // Optimistically update the cache to empty state
      queryClient.setQueryData(['notifications', userEmail], (oldData: NotificationResponse | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          notifications: [],
          unreadCount: 0,
        };
      });
      // Also invalidate to ensure fresh data on next fetch
      queryClient.invalidateQueries({ queryKey: ['notifications', userEmail] });
    },
  });

  const notifications = notificationData?.notifications || [];
  const unreadCount = notificationData?.unreadCount || 0;

  return {
    notifications,
    unreadCount,
    isLoading,
    isFetched,
    error,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    removeNotification: removeNotificationMutation.mutate,
    removeAllNotifications: removeAllNotificationsMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isRemoving: removeNotificationMutation.isPending,
    isRemovingAll: removeAllNotificationsMutation.isPending,
    refetch: () => queryClient.invalidateQueries({ queryKey: ['notifications', userEmail] }),
  };
};
